import React, { Suspense, lazy } from 'react';
import { compose } from 'redux';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import { PapiUser } from '@wix/da-papi-types';
import ProfileIdentityMobile from '@wix/da-shared-react/pkg/ProfileIdentityMobile';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import ChunkLoadingPopover from '../_partials/ChunkLoadingPopover';
import { FocusTarget } from '../AboutMe/AboutMeEditPopup/types';
import AvatarEdit from './AvatarEdit';
import ProfileStats from './ProfileStats';

import s from './ProfileIdentity.scss';

export interface Props {
  profileOwnerUser: PapiUser;
  stats: object;
  tagline: string;
  url: string;
  className?: string;
  isCurrentUserProfileOwner: boolean;
  isMobile?: boolean;
  aboutModuleId?: any;
}

export interface State {
  isAboutMeDialogOpen: boolean;
}

const LoadableAboutMeEditPopup = lazy(
  () => import(/* webpackChunkName: "owner" */ '../AboutMe/AboutMeEditPopup')
);

class ProfileIdentity extends React.Component<Props & WithTranslation, State> {
  state = {
    isAboutMeDialogOpen: false,
  };

  renderDesktop() {
    const {
      profileOwnerUser,
      stats,
      tagline,
      className,
      aboutModuleId,
      isCurrentUserProfileOwner,
      t,
    } = this.props;
    const { isAboutMeDialogOpen } = this.state;
    return (
      <div className={classnames(s['root'], className)}>
        {isCurrentUserProfileOwner && <AvatarEdit />}
        <span>
          <UserAvatar
            size={100}
            user={profileOwnerUser}
            className={s['user-avatar']}
            withTooltip={false}
            withLink={!isCurrentUserProfileOwner}
          />
        </span>
        <div className={s['user-details']}>
          <h1 className={s['user-link']}>
            <UserLink
              className={s['user-link-text']}
              symbolClassName={s['user-link-icon']}
              user={profileOwnerUser}
              withTooltip={false}
              useCoreBadge
            />
          </h1>
          {tagline && (
            <div className={s['user-label']}>
              {tagline}
              {isCurrentUserProfileOwner && aboutModuleId && (
                <IconButton
                  className={s['tagline-edit']}
                  data-role="edit-control"
                  onClick={() => this.setState({ isAboutMeDialogOpen: true })}
                  aria-label={t('widgets.aboutMe.a11y.editTagline')}
                >
                  <EditIcon size={IconSize.SMALL} />
                </IconButton>
              )}
            </div>
          )}
          <ProfileStats {...stats} />
        </div>
        {isCurrentUserProfileOwner && isAboutMeDialogOpen && (
          <Suspense fallback={<ChunkLoadingPopover />}>
            <LoadableAboutMeEditPopup
              username={profileOwnerUser.username}
              moduleId={aboutModuleId}
              focusTarget={FocusTarget.TAGLINE}
              onClose={() => this.setState({ isAboutMeDialogOpen: false })}
            />
          </Suspense>
        )}
      </div>
    );
  }

  renderMobile() {
    const { profileOwnerUser, stats, tagline, className } = this.props;
    return (
      <ProfileIdentityMobile
        user={profileOwnerUser}
        tagline={tagline}
        className={className}
        statsComponent={<ProfileStats {...stats} />}
      />
    );
  }

  render() {
    return this.props.isMobile ? this.renderMobile() : this.renderDesktop();
  }
}

const connect = compose(withMobileContext, withTranslation());

export default connect(ProfileIdentity);
